/**
 * PlayerWishlistOverlay.tsx — read-only wishlist of another game member
 * (task I05).
 *
 * Same-game members may view each other's wishlists (server enforces
 * same-game membership, task E03). Fetched ONLY when opened; no create or
 * delete controls exist here — nobody may edit another player's wishlist.
 * External links are https-only (enforced server-side on create, E01) and
 * open in a new tab with `noopener noreferrer`. `changeTick` refetches
 * while open (the minimal game:changed payload can't say whose list
 * changed, so any change while open refetches). Dialog behavior comes from
 * the shared K01 Modal.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  ApiClientError,
  type PlayerDto,
  type WishlistItem,
} from '@shared';
import { apiV1 } from '../api';
import { Modal } from './Modal';

interface PlayerWishlistOverlayProps {
  gameId: number;
  player: PlayerDto;
  /** Bumped by the page on every game:changed — refetch while open. */
  changeTick: number;
  onClose: () => void;
}

export function PlayerWishlistOverlay({ gameId, player, changeTick, onClose }: PlayerWishlistOverlayProps) {
  const [items, setItems] = useState<WishlistItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(
    async (background: boolean): Promise<void> => {
      if (!background) setLoading(true);
      try {
        const data = await apiV1.getPlayerWishlist(gameId, player.userId);
        setItems(data.items);
        setLoadError(null);
      } catch (err) {
        setLoadError(err instanceof ApiClientError ? err.message : 'Could not load this wishlist.');
      } finally {
        if (!background) setLoading(false);
      }
    },
    [gameId, player.userId],
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  useEffect(() => {
    if (changeTick > 0) void load(true);
  }, [changeTick, load]);

  return (
    <Modal title={`${player.username}\u2019s wishlist`} onClose={onClose} blocked={loading}>
        {loading && items === null && <p className="overlay-status" aria-live="polite">Loading…</p>}

        {loadError !== null && items === null && (
          <p className="auth-error" role="alert">
            {loadError}
          </p>
        )}

        {items !== null && (
          <>
            <p className="wishlist-readonly">Read-only — only {player.username} can change this list.</p>
            <ul className="wishlist-list">
              {items.length === 0 && <li className="wishlist-empty">No items yet.</li>}
              {items.map((item) => (
                <li key={item.id} className="wishlist-item">
                  <span className="wishlist-item-name">{item.name}</span>
                  {item.url !== null && (
                    <a
                      className="wishlist-item-url"
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      link
                    </a>
                  )}
                </li>
              ))}
            </ul>
            <div className="overlay-actions">
              <button type="button" className="game-refresh" onClick={onClose}>
                Done
              </button>
            </div>
          </>
        )}
    </Modal>
  );
}
